import { usePageViewTracking } from './analytics'
import { AuthProvider } from './auth/AuthContext'
import { PortalConfigProvider } from './config/PortalConfigContext'
import { usePortalConfig } from './hooks/usePortalConfig'
import ProtectedRoute from './routing/ProtectedRoute'
import PortalUnavailable from './screens/PortalUnavailable/PortalUnavailable'
import { BrandThemeProvider } from './theme/BrandThemeProvider'
import { useBrandAssets } from './theme/useBrandAssets'
import { Suspense, lazy } from 'react'
import { Route, Switch } from 'react-router-dom'
import { ErrorBoundary } from 'shared/components/ErrorBoundary/ErrorBoundary'
import Noty from 'shared/components/Noty'

// Split the portal at the login/authenticated boundary: a visitor on /login
// never downloads the authenticated chunk (Beacon + screens), and vice versa.
const LoginRouter = lazy(() => import('./routing/LoginRouter'))
const AuthenticatedApp = lazy(() => import('./routing/AuthenticatedApp'))

const App = () => {
  usePageViewTracking()
  const portalConfig = usePortalConfig()
  const branding =
    portalConfig.kind === 'available' ? portalConfig.branding : null
  useBrandAssets(branding)

  if (portalConfig.kind === 'loading') return null
  if (portalConfig.kind === 'unavailable') return <PortalUnavailable />

  const brandName = portalConfig.branding.brandName

  return (
    <BrandThemeProvider brandColor={portalConfig.branding.brandColor}>
      <Noty id="customer-portal">
        <PortalConfigProvider
          value={{
            branding: portalConfig.branding,
            beaconId: portalConfig.beaconId,
          }}
        >
          <AuthProvider>
            {/* Catch a rejected lazy() import (e.g. a stale chunk 404ing after
                a deploy) so a chunk-load failure shows the unavailable screen
                and logs to Sentry instead of blanking the portal. */}
            <ErrorBoundary fallback={<PortalUnavailable />}>
              {/* null fallback matches the loading return above: the only time
                  this shows is the one-off chunk fetch at bootstrap. */}
              <Suspense fallback={null}>
                <Switch>
                  <Route path="/login">
                    <LoginRouter
                      brandName={brandName}
                      logoUrl={portalConfig.branding.logoUrl}
                    />
                  </Route>
                  {/* Guard outside the lazy chunk: an unauthenticated deep-link
                      visitor redirects to /login without mounting (and so
                      downloading) the authenticated chunk. */}
                  <Route>
                    <ProtectedRoute>
                      <AuthenticatedApp
                        brandName={brandName}
                        logoUrl={portalConfig.branding.logoUrl}
                      />
                    </ProtectedRoute>
                  </Route>
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </AuthProvider>
        </PortalConfigProvider>
      </Noty>
    </BrandThemeProvider>
  )
}

export default App
